import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@material-ui/core'

import { Dialog, snackbar } from 'components'
import { StringUtils } from 'utils'
import { ConfigUtils } from 'utils/config'
import { useCheckFormErrors, useApiCall, useApi } from 'hooks'
import { STRINGS, ERRORS } from 'consts'
import { ErrorTypes } from 'types'
import { ApiError } from 'errors'
import { GetStoresResponse, Store } from 'features/Main/Stores/types'
import { getStores } from 'features/Main/Stores/api'
import { hasRole, Role as RoleEnum } from 'utils/roles'

import { CreateAdminPayload, NewAdminFormData, Role, CreateAdminResponse, Admin } from '../../types'
import { createAdmin } from '../../api'
import { roleLabelsMap } from '../../constants'

const rules = {
  email: [
    {
      validate: (email: string) => StringUtils.isEmail(email),
      message: 'Debe ser un mail válido.',
    },
  ],
  password: [
    {
      validate: (password: string) => password && password.length >= STRINGS.PASSWORD_MIN_LENGTH,
      message: `La contraseña debe ser al menos de ${STRINGS.PASSWORD_MIN_LENGTH} caracteres.`,
    },
  ],
  confirmPassword: [
    {
      validate: (confirmPassword: string, fields: Omit<NewAdminFormData, 'roleId'>) =>
        confirmPassword && fields.password === confirmPassword,
      message: `Las contraseñas deben coincidir.`,
    },
  ],
  roleId: [
    {
      validate: (roleId: number) => roleId > 0,
      message: `Debe seleccionarse un rol`,
    },
  ],
}

const getCreateAdminErrorMessage = (err: ApiError): string => {
  switch (err.type) {
    case ErrorTypes.EmailAddressInUse:
      return 'Este email ya se encuentra en uso'
    case ErrorTypes.ValidationError:
      return err.validationKeys?.includes('admin.email')
        ? 'Ingresar un email válido'
        : ERRORS.GENERIC_ERROR_MESSAGE
    default:
      return ERRORS.GENERIC_ERROR_MESSAGE
  }
}

interface NewAdminDialogProps {
  handleClose: () => void
  onDone: () => void
  roles: Role[]
  currentAdmin?: Admin
}

const NewAdminDialog: React.FC<NewAdminDialogProps> = ({
  handleClose,
  onDone,
  roles,
  currentAdmin,
}) => {
  const isSuperAdmin = hasRole(currentAdmin, [RoleEnum.SuperAdmin])
  const filteredRoles = isSuperAdmin
    ? roles
    : roles.filter(role => role.name !== 'super_admin' && role.name !== 'admin')
  const [createAdminApi, isCreateAdminLoading] = useApiCall<
    CreateAdminPayload,
    CreateAdminResponse
  >(createAdmin)

  const getStoresResponseGetter = (responseData: GetStoresResponse) => responseData?.stores ?? []

  const [{ data: stores, isLoading: isLoadingStores }] = useApi<GetStoresResponse, Store[]>(
    getStores,
    getStoresResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Buscar el rol "admin" por defecto
  const defaultAdminRole = filteredRoles.find(role => role.name === 'admin')
  const defaultRoleId = defaultAdminRole?.id || 0
  const [selectedRole, setSelectedRole] = useState<number>(defaultRoleId)
  const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([])
  const [selectedStoreId, setSelectedStoreId] = useState<number>(0) // Para employee (single select)
  const [allStoresSelected, setAllStoresSelected] = useState<boolean>(false) // Para analyst (todos los locales)
  const [reportsEnabled, setReportsEnabled] = useState<boolean>(false)
  const [reviewsCanRespond, setReviewsCanRespond] = useState<boolean>(false)
  const [criticalReviewAlertsEnabled, setCriticalReviewAlertsEnabled] = useState<boolean>(false)
  const [selectedReportFrequencies, setSelectedReportFrequencies] = useState<string[]>([])

  const handleEmailChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setEmail(e.target.value)
  }

  const handlePasswordChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setPassword(e.target.value)
  }

  const handleConfirmPasswordChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setConfirmPassword(e.currentTarget.value)
  }

  const handleRoleSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newRoleId = event.target.value as number
    setSelectedRole(newRoleId)
    // Reset stores cuando cambia el rol
    setSelectedStoreIds([])
    setSelectedStoreId(0)
    setAllStoresSelected(false)
  }

  const handleStoreSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedStoreIds(event.target.value as number[])
  }

  const handleStoreSelectSingle = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedStoreId(event.target.value as number)
  }

  const handleAllStoresToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    setAllStoresSelected(checked)
    if (checked) {
      setSelectedStoreIds([]) // Limpiar selección cuando se marca "todos"
    }
  }

  // Validar según el rol
  const selectedRoleName = filteredRoles.find(r => r.id === selectedRole)?.name
  const isEmployee = selectedRoleName === 'employee'
  const isAnalyst = selectedRoleName === 'analyst'
  const isMarketing = selectedRoleName === 'marketing'
  const canConfigureReviewsRespond = isAnalyst || isMarketing
  const storeIdsError = isEmployee && selectedStoreId === 0
  const storeIdsErrorAnalyst = isAnalyst && !allStoresSelected && selectedStoreIds.length === 0
  const storesNotLoadedError = isAnalyst && allStoresSelected && (!stores || stores.length === 0)

  // Campos requeridos: storeIds solo si es empleado
  const requiredFields = isEmployee
    ? ['email', 'password', 'confirmPassword', 'roleId']
    : ['email', 'password', 'confirmPassword', 'roleId']

  // Construir fields y rules dinámicamente según si es empleado
  const baseFields = {
    email,
    password,
    confirmPassword,
    roleId: selectedRole,
  }

  const fields = baseFields
  const activeRules = rules

  const { errors, hasErrors, isAnyFieldEmpty } = useCheckFormErrors(
    fields,
    activeRules,
    requiredFields,
  )
  const isSubmitDisabled =
    hasErrors ||
    isAnyFieldEmpty ||
    isCreateAdminLoading ||
    isLoadingStores ||
    selectedRole === 0 ||
    storeIdsError ||
    storeIdsErrorAnalyst ||
    storesNotLoadedError

  const onCreateAdmin = async () => {
    try {
      let storeIdsToSend: number[] | undefined

      if (isEmployee) {
        if (selectedStoreId > 0) {
          storeIdsToSend = [selectedStoreId]
        }
      } else if (isAnalyst) {
        if (allStoresSelected) {
          // Enviar los IDs de todas las tiendas disponibles en el selector
          if (stores && stores.length > 0) {
            storeIdsToSend = stores.map(s => s.id)
            console.log('Enviando todas las tiendas:', storeIdsToSend)
          } else {
            snackbar.show(
              'Error: No se pudieron cargar las tiendas. Por favor, intente nuevamente.',
            )
            return
          }
        } else if (selectedStoreIds.length > 0) {
          storeIdsToSend = selectedStoreIds
        }
      }

      console.log('Datos a enviar:', { email, roleId: selectedRole, storeIds: storeIdsToSend })

      const hasWeekly = selectedReportFrequencies.includes('weekly')
      const hasMonthly = selectedReportFrequencies.includes('monthly')

      await createAdminApi({
        email,
        password,
        roleId: selectedRole,
        storeIds: storeIdsToSend,
        reportsEnabled,
        reportFrequencyWeekly: reportsEnabled ? hasWeekly : false,
        reportFrequencyBiweekly: false,
        reportFrequencyMonthly: reportsEnabled ? hasMonthly : false,
        ...(canConfigureReviewsRespond && ConfigUtils.reviews.enabled ? { reviewsCanRespond } : {}),
        ...(ConfigUtils.reviews.enabled ? { criticalReviewAlertsEnabled } : {}),
      })
      onDone()
    } catch (err) {
      snackbar.show(getCreateAdminErrorMessage(err as ApiError))
    }
  }

  return (
    <Dialog
      title="Nuevo administrador"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isCreateAdminLoading}
      okButtonText="Crear"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onCreateAdmin}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          <TextField
            label="Email"
            placeholder="ejemplo@admin.com"
            value={email}
            fullWidth
            onChange={handleEmailChange}
            error={errors.email.hasError}
            helperText={errors.email.message}
            disabled={isCreateAdminLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Contraseña"
            type="password"
            fullWidth
            value={password}
            onChange={handlePasswordChange}
            error={errors.password.hasError}
            helperText={errors.password.message}
            disabled={isCreateAdminLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Repetir contraseña"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            error={errors.confirmPassword.hasError}
            helperText={errors.confirmPassword.message}
            disabled={isCreateAdminLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Rol</InputLabel>
            <Select value={selectedRole} onChange={handleRoleSelect} fullWidth>
              {filteredRoles.map(role => (
                <MenuItem key={role.id} value={role.id}>
                  {roleLabelsMap[role.name]?.label || role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {isEmployee && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={storeIdsError}>
              <InputLabel>Local *</InputLabel>
              <Select value={selectedStoreId} onChange={handleStoreSelectSingle} fullWidth required>
                <MenuItem value={0}>
                  <em>Seleccionar local</em>
                </MenuItem>
                {stores.map(store => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.id} - {store.name}
                  </MenuItem>
                ))}
              </Select>
              {storeIdsError && (
                <span style={{ color: 'red', fontSize: '12px' }}>
                  Debe seleccionarse un local para empleados
                </span>
              )}
            </FormControl>
          </Grid>
        )}
        {isAnalyst && (
          <Grid item xs={12}>
            <FormControl fullWidth error={storeIdsErrorAnalyst}>
              {!allStoresSelected && (
                <>
                  <InputLabel>Locales *</InputLabel>
                  <Select
                    multiple
                    value={selectedStoreIds}
                    onChange={handleStoreSelect}
                    fullWidth
                    required
                    renderValue={selected =>
                      (selected as number[])
                        .map(id => {
                          const store = stores.find(s => s.id === id)
                          return store ? `${store.id} - ${store.name}` : ''
                        })
                        .join(', ')
                    }
                  >
                    {stores.map(store => (
                      <MenuItem key={store.id} value={store.id}>
                        <Checkbox checked={selectedStoreIds.indexOf(store.id) > -1} />
                        {store.id} - {store.name}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allStoresSelected}
                    onChange={handleAllStoresToggle}
                    color="primary"
                  />
                }
                label="Todos los locales"
              />
              {storeIdsErrorAnalyst && (
                <span style={{ color: 'red', fontSize: '12px' }}>
                  Debe seleccionarse &quot;Todos los locales&quot; o al menos un local
                </span>
              )}
              {storesNotLoadedError && (
                <span style={{ color: 'red', fontSize: '12px' }}>
                  Cargando tiendas, por favor espere...
                </span>
              )}
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={reportsEnabled}
                  onChange={e => {
                    const { checked } = e.target
                    setReportsEnabled(checked)
                    if (!checked) {
                      setSelectedReportFrequencies([])
                    }
                  }}
                  color="primary"
                />
              }
              label="Recibir reportes automáticos del dashboard"
            />
            {reportsEnabled && (
              <FormControl fullWidth style={{ marginTop: 8 }}>
                <InputLabel>Frecuencias de reporte</InputLabel>
                <Select
                  multiple
                  value={selectedReportFrequencies}
                  onChange={e => setSelectedReportFrequencies(e.target.value as string[])}
                  renderValue={selected =>
                    (selected as string[])
                      .map(value => {
                        if (value === 'weekly') return 'Reporte semanal'
                        if (value === 'monthly') return 'Reporte mensual (cierre de mes)'
                        return value
                      })
                      .join(', ')
                  }
                >
                  <MenuItem value="weekly">
                    <Checkbox checked={selectedReportFrequencies.includes('weekly')} />
                    Reporte semanal
                  </MenuItem>
                  <MenuItem value="monthly">
                    <Checkbox checked={selectedReportFrequencies.includes('monthly')} />
                    Reporte mensual (cierre de mes)
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </FormGroup>
        </Grid>
        {ConfigUtils.reviews.enabled && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={criticalReviewAlertsEnabled}
                  onChange={e => setCriticalReviewAlertsEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="Recibir alertas de reseñas negativas"
            />
          </Grid>
        )}
        {canConfigureReviewsRespond && ConfigUtils.reviews.enabled && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Permisos de Reseñas
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={reviewsCanRespond}
                  onChange={e => setReviewsCanRespond(e.target.checked)}
                  color="primary"
                />
              }
              label="Permitir responder reseñas"
            />
            {!reviewsCanRespond && (
              <Typography variant="caption" color="textSecondary" display="block">
                Solo Vista — acceso de lectura sin poder modificar reseñas
              </Typography>
            )}
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}

NewAdminDialog.defaultProps = {
  currentAdmin: undefined,
}

export { NewAdminDialog }
